import { PRESIDENT_ADDRESS_PAGE } from "@config/presidentAddress";
import { AdsImu2 } from "@elements/Advertisement/variant/AdsImu";
import { PresidentAddressIcon } from "@elements/Banner/PresidentAddressIcon";
import Heading from "@elements/Typography/Heading";
import BHBasePage, { type BHBasePageProps } from "@pages/BHBasePage";
import ErrorPage from "@pages/ErrorPage";
import WidgetWrapper from "@pages/Home/HomePageLayout/WidgetWrapper";
import NotFound from "@pages/NotFound";
import type { TRouteWithoutRedirect } from "@sphtech/web2-core/ssr";
import {
  CustomContext,
  ResponseType,
  useRouteContext,
} from "@sphtech/web2-core/ssr";
import { PageAdTargetingTypeEnum } from "@typings/Ads.d";

import LiveBlog from "./Components/LiveBlog";
import PresidentAddressArticleList from "./Components/PresidentAddressArticleList";
import { PresidentAddressPayloadResponseType } from "./PresidentAddress.server";
import PresidentAddressMetaData from "./PresidentAddressMetaData";

export function PresidentAddress() {
  const routeContext: CustomContext<
    TRouteWithoutRedirect<PresidentAddressPayloadResponseType, string>
  > = useRouteContext();
  const dataLoaderResponse = routeContext.context;

  if (dataLoaderResponse.type === ResponseType.CLIENT_ERROR) {
    return <NotFound />;
  } else if (dataLoaderResponse.type === ResponseType.SERVER_ERROR) {
    return <ErrorPage message="Something went wrong" />;
  }

  const { tagsData = [], liveBlog } = dataLoaderResponse.payload;

  const adsSectionName = PRESIDENT_ADDRESS_PAGE;
  const { gaData, metaTags } = PresidentAddressMetaData(adsSectionName);
  const pageAdTargetValue = PageAdTargetingTypeEnum.LISTING;

  const basePageProps: BHBasePageProps = {
    uniqueSectionName: adsSectionName,
    gaData: gaData,
    metaTags: metaTags,
    pageAdTargetValue: pageAdTargetValue,
    hasLb1Ads: true,
    showNDRBanner: false,
    bodyWrapperStyle: "my-0 lg:my-0 py-3 lg:py-md",
  };

  return (
    <BHBasePage {...basePageProps}>
      <WidgetWrapper>
        <div>
          <div className="border-b-[1px] border-grey-400 pb-[10px] md:pb-[20px] mb-[10px] md:mb-[20px]">
            <div className="flex gap-xs items-center">
              <PresidentAddressIcon
                isLive={liveBlog ? true : false}
                variant="heading"
              />
              <Heading
                className="text-black-800 text-[18px] md:text-[30px] font-semibold font-secondary"
                Element="h1"
              >
                Parlimen Ke-15
              </Heading>
            </div>
          </div>
        </div>
        <div className="flex flex-col lg:flex-row gap-[40px]">
          <div className="w-full">
            {liveBlog ? (
              <div className="w-full h-full">
                <LiveBlog variant="body" liveBlogContent={liveBlog} />
              </div>
            ) : (
              <PresidentAddressArticleList
                articles={tagsData}
                adsSectionName={adsSectionName}
                variant="body"
              />
            )}
          </div>
          <div className="flex flex-col gap-10 lg:max-w-[300px] flex-1">
            <div className="flex-1 h-full">
              <AdsImu2
                uniqueSectionName={adsSectionName}
                pageAdTargetType={PageAdTargetingTypeEnum.LISTING}
              />
            </div>
            {liveBlog && tagsData.length > 0 && (
              <div className="flex flex-col gap-5">
                <span className="text-[20px] font-secondary text-black-800 font-medium">
                  Artikel
                </span>
                <PresidentAddressArticleList
                  articles={tagsData}
                  adsSectionName={adsSectionName}
                  variant="sidebar"
                />
              </div>
            )}
          </div>
        </div>
      </WidgetWrapper>
    </BHBasePage>
  );
}
